/**
 * LeadLoop approval actions — review/edit, regenerate, cancel.
 *
 * An AI-proposed action waits on the lead's LeadLoop card. Review & Edit
 * opens the draft (email subject/body, or call notes/instructions for
 * outgoing-call style actions) for human editing before Approve/Reject.
 * Regenerate sends feedback to the simulated AI, which revises the draft.
 * Everything only writes demo records — approving never sends anything.
 */

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { lccApi, type LccApproval, type LccApprovalDraft } from '@/lib/lcc-api';
import { formatLccLabel } from './lcc-shared';
import { useLccInvalidate } from './lead-actions';

export function approvalDraft(approval: LccApproval): LccApprovalDraft {
  const draft = approval.metadata?.draft;
  return typeof draft === 'object' && draft !== null ? (draft as LccApprovalDraft) : {};
}

function isEmailApproval(approval: LccApproval): boolean {
  return approval.itemType === 'outbound_email_draft';
}

/** Call-style actions carry editable notes/instructions instead of a body. */
function isCallApproval(approval: LccApproval): boolean {
  return approval.itemType === 'verification_request' || approval.itemType === 'outbound_call';
}

const inputCls =
  'flex h-9 w-full rounded-md border border-input bg-background px-2.5 text-[13px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';
const textareaCls =
  'flex w-full resize-y rounded-md border border-input bg-background px-2.5 py-1.5 text-[13px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export function ApprovalReviewDialog({
  approval,
  open,
  onOpenChange,
}: {
  approval: LccApproval;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const editable = isEmailApproval(approval) || isCallApproval(approval);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [instructions, setInstructions] = useState('');

  useEffect(() => {
    if (open) {
      const draft = approvalDraft(approval);
      setSubject(draft.subject ?? approval.title);
      setBody(draft.body ?? approval.description ?? '');
      setInstructions(draft.instructions ?? approval.description ?? '');
    }
  }, [open, approval]);

  const save = useMutation({
    mutationFn: () =>
      lccApi.updateApproval(
        approval.id,
        isEmailApproval(approval) ? { draft: { subject, body } } : { draft: { instructions } },
      ),
    onSuccess: invalidate,
  });
  const decide = useMutation({
    mutationFn: async (decision: 'approve' | 'reject') => {
      // Persist any edits first so the approved record matches what was read.
      if (editable) {
        await lccApi.updateApproval(
          approval.id,
          isEmailApproval(approval) ? { draft: { subject, body } } : { draft: { instructions } },
        );
      }
      return lccApi.decideApproval(approval.id, decision);
    },
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  const busy = save.isPending || decide.isPending;
  const error = save.error ?? decide.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{approval.title}</DialogTitle>
          <DialogDescription>
            {formatLccLabel(approval.itemType)} proposed by{' '}
            {approval.requestedByType === 'ai' ? 'the AI' : 'a teammate'} — approving only records
            the decision; the demo never sends anything.
          </DialogDescription>
        </DialogHeader>

        {approval.description && (
          <p className="text-[12.5px] leading-snug text-muted-foreground">{approval.description}</p>
        )}

        {isEmailApproval(approval) && (
          <div className="space-y-2">
            <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
              Subject
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
              Email body
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className={textareaCls} />
            </label>
          </div>
        )}
        {isCallApproval(approval) && (
          <label className="flex flex-col gap-1 text-[12px] font-medium text-muted-foreground">
            Call notes / instructions
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={6}
              className={textareaCls}
            />
          </label>
        )}
        {!editable && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12.5px] text-muted-foreground">
            This action type has no editable draft — approve or reject it as proposed.
          </div>
        )}

        {error != null && (
          <p className="text-[12px] text-destructive">
            {error instanceof Error ? error.message : 'Action failed'}
          </p>
        )}
        <DialogFooter className="flex-wrap gap-1.5">
          {editable && (
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => save.mutate()}>
              {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
              Save Edits
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={busy}
            onClick={() => decide.mutate('reject')}
          >
            <ThumbsDown className="h-3.5 w-3.5" aria-hidden />
            Reject
          </Button>
          <Button type="button" size="sm" className="gap-1.5" disabled={busy} onClick={() => decide.mutate('approve')}>
            {decide.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
            )}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ApprovalRegenerateDialog({
  approval,
  open,
  onOpenChange,
}: {
  approval: LccApproval;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const invalidate = useLccInvalidate();
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (open) setFeedback('');
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => lccApi.regenerateApproval(approval.id, feedback),
    onSuccess: () => {
      invalidate();
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" aria-hidden />
            Regenerate with feedback
          </DialogTitle>
          <DialogDescription>
            Tell the AI what to change about "{approval.title}". The revision is simulated — the
            draft updates instantly and your feedback is kept with the approval.
          </DialogDescription>
        </DialogHeader>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
          autoFocus
          placeholder="e.g. Shorter, friendlier, and offer a Thursday time slot..."
          className={textareaCls}
        />
        {mutation.isError && (
          <p className="text-[12px] text-destructive">
            {mutation.error instanceof Error ? mutation.error.message : 'Regeneration failed'}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            disabled={!feedback.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
            )}
            Regenerate (simulated)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Small status chip used on loop cards while an approval is pending. */
export function ApprovalPendingBadge() {
  return (
    <Badge variant="warning" className="gap-1">
      Awaiting approval
    </Badge>
  );
}
